import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { CMSDomainInfo } from "../../types";
import { CNAME_TARGET } from "../../config/apiConfig";

// Constants
const MAX_SUBDOMAIN_LENGTH = 100;

interface DomainSetupModalProps {
  apiDomain: CMSDomainInfo | null;
  onClose: () => void;
  onSetDomain: (domain: string) => Promise<CMSDomainInfo>;
  onVerifyDomain: () => Promise<boolean>;
  onRemove?: () => Promise<void>;
  // Subdomain props
  subdomain: string | null;
  subdomainSuffix: string; // e.g., ".mehey.com" - from API
  onSetSubdomain: (subdomain: string) => Promise<void>;
  defaultSubdomain?: string;
  // Auto re-publish when Done
  isSitePublished?: boolean;
  isCurrentSitePublished?: boolean; // Is THIS site the one that's published?
  publishedSiteName?: string | null; // Name of the site that's currently published (if different)
  onPublish?: () => Promise<void>;
  onPublishReplace?: () => Promise<void>; // Unpublish other site and publish this one
}

// Flow: 1) Custom Domain + TXT → 2) Subdomain → 3) CNAME
const steps = ["Custom Domain", "Subdomain", "Configure DNS"];

export const DomainSetupModal: React.FC<DomainSetupModalProps> = ({
  apiDomain,
  onClose,
  onSetDomain,
  onVerifyDomain,
  onRemove,
  subdomain: initialSubdomain,
  subdomainSuffix,
  onSetSubdomain,
  defaultSubdomain,
  isSitePublished,
  isCurrentSitePublished,
  publishedSiteName,
  onPublish,
  onPublishReplace,
}) => {
  // Determine initial step based on current state
  const getInitialStep = () => {
    // If domain is verified
    if (apiDomain?.domain && apiDomain.is_domain_verified) {
      // If subdomain is set, go to step 2 (CNAME)
      if (initialSubdomain) return 2;
      // Otherwise go to step 1 (Subdomain)
      return 1;
    }
    // If domain exists but not verified, stay at step 0
    // If no domain, start at step 0
    return 0;
  };

  const [activeStep, setActiveStep] = useState(getInitialStep());

  // Custom domain state
  const [domain, setDomain] = useState(apiDomain?.domain || "");
  const [txtToken, setTxtToken] = useState(apiDomain?.challenge || "");
  const [isTxtValidated, setIsTxtValidated] = useState(apiDomain?.is_domain_verified || false);
  const [isSettingDomain, setIsSettingDomain] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedTxt, setCopiedTxt] = useState(false);
  const [copiedCname, setCopiedCname] = useState(false);

  // Subdomain state
  const [subdomain, setSubdomain] = useState(initialSubdomain || defaultSubdomain || "");
  const [isSubdomainSaved, setIsSubdomainSaved] = useState(!!initialSubdomain);
  const [isSavingSubdomain, setIsSavingSubdomain] = useState(false);
  const [subdomainError, setSubdomainError] = useState<string | null>(null);

  // Auto re-publish state
  const [isPublishing, setIsPublishing] = useState(false);

  // Track last fetched domain to prevent duplicate API calls
  const lastFetchedDomain = useRef<string>(apiDomain?.domain || "");

  // Reusable copy to clipboard helper
  const copyToClipboard = useCallback(async (text: string, onSuccess: () => void) => {
    try {
      await navigator.clipboard.writeText(text);
      onSuccess();
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      onSuccess();
    }
  }, []);

  // Initialize from apiDomain when it changes
  useEffect(() => {
    if (apiDomain) {
      setDomain(apiDomain.domain || "");
      setTxtToken(apiDomain.challenge || "");
      setIsTxtValidated(apiDomain.is_domain_verified || false);
      lastFetchedDomain.current = apiDomain.domain || "";
    }
  }, [apiDomain]);

  // Update subdomain when initialSubdomain changes
  useEffect(() => {
    if (initialSubdomain) {
      setSubdomain(initialSubdomain);
      setIsSubdomainSaved(true);
    }
  }, [initialSubdomain]);

  // Custom domain handlers
  const handleDomainChange = (value: string) => {
    const cleaned = value
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/\/+$/, "")
      .trim();
    setDomain(cleaned);
    setIsTxtValidated(false);
    setError(null);
    lastFetchedDomain.current = "";
    setTxtToken("");
  };

  // Auto-fetch challenge from API when domain changes (debounced)
  useEffect(() => {
    if (!domain || lastFetchedDomain.current === domain) return;

    const timer = setTimeout(async () => {
      if (lastFetchedDomain.current === domain) return;

      setIsSettingDomain(true);
      setError(null);
      try {
        const result = await onSetDomain(domain);
        lastFetchedDomain.current = domain;
        setTxtToken(result.challenge);
        setIsTxtValidated(result.is_domain_verified);
        if (result.is_domain_verified) {
          setActiveStep(1); // Go to Subdomain step
        }
      } catch (err) {
        setError("Failed to set domain. Please try again.");
      } finally {
        setIsSettingDomain(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [domain, onSetDomain]);

  const handleValidate = async () => {
    if (!domain) {
      setError("Please enter a domain");
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const result = await onVerifyDomain();
      setIsTxtValidated(result);
      if (result) {
        setActiveStep(1); // Go to Subdomain step
      } else {
        setError("Domain verification failed. Please check your TXT record and try again.");
      }
    } catch (err) {
      setError("Failed to validate domain. Please try again.");
      setIsTxtValidated(false);
    } finally {
      setIsValidating(false);
    }
  };

  // Subdomain handlers
  const handleSubdomainChange = (value: string) => {
    const cleaned = value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "")
      .replace(/^-+/, "")
      .substring(0, MAX_SUBDOMAIN_LENGTH);
    setSubdomain(cleaned);
    setIsSubdomainSaved(false);
    setSubdomainError(null);
  };

  const handleSaveSubdomain = async () => {
    if (!subdomain) {
      setSubdomainError("Please enter a subdomain");
      return;
    }

    setIsSavingSubdomain(true);
    setSubdomainError(null);

    try {
      await onSetSubdomain(subdomain);
      setIsSubdomainSaved(true);
      setActiveStep(2); // Go to CNAME step
    } catch (err) {
      setSubdomainError("Failed to save subdomain. It may already be taken.");
    } finally {
      setIsSavingSubdomain(false);
    }
  };

  const handleRemove = async () => {
    if (!onRemove) return;

    setIsRemoving(true);
    try {
      await onRemove();
      setDomain("");
      setTxtToken("");
      setIsTxtValidated(false);
      setActiveStep(0); // Go back to domain step
    } catch (err) {
      setError("Failed to remove domain. Please try again.");
    } finally {
      setIsRemoving(false);
    }
  };

  const handleCopyTxt = () => {
    copyToClipboard(txtToken, () => {
      setCopiedTxt(true);
      setTimeout(() => setCopiedTxt(false), 2000);
    });
  };

  const handleCopyCname = () => {
    copyToClipboard(CNAME_TARGET, () => {
      setCopiedCname(true);
      setTimeout(() => setCopiedCname(false), 2000);
    });
  };

  // Get CNAME host name - return exactly what customer entered
  const getCnameHost = (): string => {
    return domain || "@";
  };

  const hasCustomDomain = !!(apiDomain?.domain && apiDomain.domain.trim());

  // Handle Done button - auto re-publish if site is already published
  const handleDone = async () => {
    // Only re-publish if THIS site is currently published
    if (isCurrentSitePublished && onPublish) {
      setIsPublishing(true);
      try {
        await onPublish();
      } catch (err) {
        console.error("Error re-publishing site:", err);
        // Still close the modal even if publish fails
      } finally {
        setIsPublishing(false);
      }
    }
    onClose();
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(4px)",
        }}
      />

      {/* Modal */}
      <Paper
        elevation={24}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 550,
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderRadius: 3,
          animation: "fadeIn 0.2s ease-out",
          "@keyframes fadeIn": {
            from: { opacity: 0, transform: "scale(0.95)" },
            to: { opacity: 1, transform: "scale(1)" },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Domain Settings
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Stepper */}
        <Box sx={{ px: 3, pt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label} completed={index === 0 ? isTxtValidated : index === 1 ? isSubdomainSaved : false}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, overflow: "auto", flex: 1 }}>
          {/* Step 1: Custom Domain + TXT */}
          {activeStep === 0 && (
            <Box
              sx={(theme) => ({
                bgcolor: isTxtValidated ? alpha(theme.palette.success.main, 0.08) : "grey.50",
                borderRadius: 2,
                p: 2.5,
                border: "1px solid",
                borderColor: isTxtValidated ? alpha(theme.palette.success.main, 0.3) : "grey.200",
              })}
            >
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
                Step 1: Set Your Custom Domain
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Enter your domain (e.g., <strong>example.com</strong>) and verify ownership with a TXT record.
              </Typography>

              {/* Domain Input */}
              <TextField
                fullWidth
                value={domain}
                onChange={(e) => handleDomainChange(e.target.value)}
                placeholder="example.com"
                size="small"
                disabled={isTxtValidated}
                helperText="Enter the full domain without https://"
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />

              {/* TXT Record Info - Show only when domain is entered */}
              {domain && txtToken && (
                <Box
                  sx={{
                    bgcolor: "white",
                    border: "1px solid",
                    borderColor: "grey.300",
                    borderRadius: 1.5,
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Typography variant="caption" fontWeight={600} sx={{ display: "block", mb: 1 }}>
                    Add this TXT record to verify ownership:
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Type
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      TXT
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontFamily: "monospace" }}>
                      @
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Value
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                      {isSettingDomain ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{
                            fontFamily: "monospace",
                            color: "primary.main",
                            wordBreak: "break-all",
                            fontSize: "0.75rem",
                          }}
                        >
                          {txtToken}
                        </Typography>
                      )}
                      <IconButton size="small" onClick={handleCopyTxt}>
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                    {copiedTxt && (
                      <Typography variant="caption" color="success.main">
                        Copied!
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Status */}
              {domain && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  {isTxtValidated ? (
                    <>
                      <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
                      <Typography variant="body2" color="success.main">
                        Domain verified
                      </Typography>
                    </>
                  ) : (
                    <>
                      <ErrorOutlineIcon sx={{ fontSize: 18, color: "warning.main" }} />
                      <Typography variant="body2" color="warning.main">
                        Pending verification
                      </Typography>
                    </>
                  )}
                </Box>
              )}

              {/* Buttons */}
              <Box sx={{ display: "flex", gap: 1 }}>
                {isTxtValidated ? (
                  <Button
                    variant="contained"
                    onClick={() => setActiveStep(1)}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >
                    Next: Set Subdomain
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleValidate}
                    disabled={!domain || !txtToken || isValidating}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >
                    {isValidating ? <CircularProgress size={20} color="inherit" /> : "Verify Domain"}
                  </Button>
                )}
              </Box>

              {error && (
                <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
                  {error}
                </Typography>
              )}
            </Box>
          )}

          {/* Step 2: Subdomain */}
          {activeStep === 1 && (
            <Box
              sx={(theme) => ({
                bgcolor: isSubdomainSaved ? alpha(theme.palette.success.main, 0.08) : "grey.50",
                borderRadius: 2,
                p: 2.5,
                border: "1px solid",
                borderColor: isSubdomainSaved ? alpha(theme.palette.success.main, 0.3) : "grey.200",
              })}
            >
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
                Step 2: Set Your Subdomain
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Choose a subdomain for your site. This will be your site's URL on our platform.
              </Typography>

              {/* Subdomain Input */}
              <TextField
                fullWidth
                value={subdomain}
                onChange={(e) => handleSubdomainChange(e.target.value)}
                placeholder="myclinic"
                size="small"
                disabled={isSubdomainSaved}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="body2" color="text.secondary">
                        {subdomainSuffix}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />

              {/* Tips */}
              <Box
                sx={(theme) => ({
                  bgcolor: alpha(theme.palette.info.main, 0.08),
                  border: "1px solid",
                  borderColor: alpha(theme.palette.info.main, 0.3),
                  borderRadius: 1,
                  p: 1.5,
                  mb: 2,
                })}
              >
                <Typography variant="caption" fontWeight={600} color="info.dark" sx={{ display: "block", mb: 0.5 }}>
                  Tips
                </Typography>
                <Typography variant="caption" color="info.dark" component="div">
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    <li>Use lowercase letters (a-z), numbers (0-9), and hyphens (-)</li>
                    <li>Maximum {MAX_SUBDOMAIN_LENGTH} characters</li>
                  </Box>
                </Typography>
              </Box>

              {/* Preview */}
              <Box
                sx={(theme) => ({
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  borderRadius: 1,
                  p: 1.5,
                  mb: 2,
                })}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  Your site will be at:
                </Typography>
                <Typography variant="body1" fontWeight={600} color="primary.main" sx={{ fontFamily: "monospace" }}>
                  {subdomain || "myclinic"}
                  {subdomainSuffix}
                </Typography>
              </Box>

              {/* Status */}
              {isSubdomainSaved && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
                  <Typography variant="body2" color="success.main">
                    Subdomain saved
                  </Typography>
                </Box>
              )}

              {/* Buttons */}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep(0)}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Back
                </Button>
                {isSubdomainSaved ? (
                  <>
                    <Button
                      variant="outlined"
                      onClick={() => setIsSubdomainSaved(false)}
                      sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => setActiveStep(2)}
                      sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                      Next: Configure DNS
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSaveSubdomain}
                    disabled={!subdomain || isSavingSubdomain}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >
                    {isSavingSubdomain ? <CircularProgress size={20} color="inherit" /> : "Save & Next"}
                  </Button>
                )}
              </Box>

              {subdomainError && (
                <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
                  {subdomainError}
                </Typography>
              )}
            </Box>
          )}

          {/* Step 3: CNAME Configuration */}
          {activeStep === 2 && (
            <Box
              sx={{
                bgcolor: "grey.50",
                borderRadius: 2,
                p: 2.5,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
                Step 3: Configure DNS
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Add this CNAME record to point your domain to our servers:
              </Typography>

              <Box
                sx={{
                  bgcolor: "white",
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: 1.5,
                  p: 2,
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Type
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    CNAME
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Name / Host
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontFamily: "monospace" }}>
                    {getCnameHost()}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Target
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ fontFamily: "monospace", color: "primary.main" }}
                    >
                      {CNAME_TARGET}
                    </Typography>
                    <IconButton size="small" onClick={handleCopyCname}>
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
                {copiedCname && (
                  <Typography
                    variant="caption"
                    color="success.main"
                    sx={{ display: "block", textAlign: "right", mt: 0.5 }}
                  >
                    Copied!
                  </Typography>
                )}
              </Box>

              {/* Cloudflare Note */}
              <Box
                sx={(theme) => ({
                  bgcolor: alpha(theme.palette.info.main, 0.08),
                  border: "1px solid",
                  borderColor: alpha(theme.palette.info.main, 0.3),
                  borderRadius: 1,
                  p: 1.5,
                  mb: 2,
                })}
              >
                <Typography variant="caption" fontWeight={600} color="info.dark" sx={{ display: "block", mb: 0.5 }}>
                  Cloudflare Users
                </Typography>
                <Typography variant="caption" color="info.dark">
                  Set Proxy status to "DNS only" (grey cloud), not "Proxied" (orange cloud).
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep(1)}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Back
                </Button>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                DNS propagation can take up to 48 hours.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2.5,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "grey.50",
          }}
        >
          <Box>
            {hasCustomDomain && onRemove && (
              <Button onClick={handleRemove} disabled={isRemoving} color="error" sx={{ textTransform: "none" }}>
                {isRemoving ? "Removing..." : "Remove Custom Domain"}
              </Button>
            )}
          </Box>
          <Button
            variant={isTxtValidated ? "contained" : "text"}
            onClick={isTxtValidated ? handleDone : onClose}
            disabled={isPublishing}
            sx={{ textTransform: "none" }}
          >
            {isPublishing ? <CircularProgress size={20} color="inherit" /> : isTxtValidated ? "Done" : "Cancel"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
